// scheduledTriggerClient — P-105: client-wrappers + cron-validation
// för scheduled_trigger-RPCs (separat från pure \`triggerScheduler.ts\`
// som är in-memory-planner utan DB).
//
// Cron-runner (server-side poller som faktiskt triggar runs) är
// infrastruktur (pg_cron eller GitHub-Actions) utanför scope för denna
// commit. Denna modul låter UI skapa/lista/disable schedules;
// runner:n läser next_run_at-kolumnen när den deploy:as.

import { callRpc } from '@/lib/supabaseTyped';
import { validateCronExpression } from '@/lib/cronValidation';
export { validateCronExpression };

export type ScheduleKind = 'interval' | 'cron';

export interface ScheduledTriggerMetadata {
  id: string;
  workflow_id: string;
  label: string;
  schedule_kind: ScheduleKind;
  interval_seconds: number | null;
  cron_expression: string | null;
  timezone: string;
  disabled_at: string | null;
  next_run_at: string | null;
  last_run_at: string | null;
  trigger_count: number;
  created_at: string;
}

export interface CreateScheduleInput {
  workflowId: string;
  label: string;
  kind: ScheduleKind;
  intervalSeconds?: number;
  cronExpression?: string;
  timezone?: string;
}

export interface CreateScheduleResult {
  success: boolean;
  id?: string;
  next_run_at?: string;
  error?: string;
}

// validateCronExpression har flyttats till @/lib/cronValidation (pure,
// utan Supabase-beroende) så scheduledTriggerRuntime kan importera den
// utan att dra in supabase-client. Re-exported ovan för bakåt-kompat.

export async function createScheduledTrigger(
  input: CreateScheduleInput,
): Promise<CreateScheduleResult> {
  if (input.kind === 'interval' && (!input.intervalSeconds || input.intervalSeconds < 60)) {
    return { success: false, error: 'interval-min-60-seconds' };
  }
  if (input.kind === 'cron') {
    const validation = validateCronExpression(input.cronExpression ?? '');
    if (!validation.ok) {
      return { success: false, error: `invalid-cron:${validation.reason}` };
    }
  }
  const { data, error } = await callRpc<CreateScheduleResult>(
    'create_scheduled_trigger',
    {
      p_workflow_id: input.workflowId,
      p_label: input.label,
      p_schedule_kind: input.kind,
      p_interval_seconds: input.intervalSeconds ?? null,
      p_cron_expression: input.cronExpression ?? null,
      p_timezone: input.timezone ?? 'UTC',
    },
  );
  if (error) return { success: false, error: error.message };
  return data ?? { success: false, error: 'empty-response' };
}

export async function listMyScheduledTriggers(
  workflowId?: string,
): Promise<{ success: boolean; triggers: ScheduledTriggerMetadata[]; error?: string }> {
  const { data, error } = await callRpc<ScheduledTriggerMetadata[]>(
    'list_my_scheduled_triggers',
    { p_workflow_id: workflowId ?? null },
  );
  if (error) return { success: false, triggers: [], error: error.message };
  return { success: true, triggers: data ?? [] };
}

export async function disableScheduledTrigger(
  id: string,
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await callRpc<{ success: boolean; error?: string }>(
    'disable_scheduled_trigger',
    { p_id: id },
  );
  if (error) return { success: false, error: error.message };
  return data ?? { success: false, error: 'empty-response' };
}
